/**
 * Channel Lineup — the ZIP-scoped half of a resolve, for the server resolver's `market` input
 * (stories and fixtures). The live page builds its locals in the browser (`local-stations.ts`).
 */

import type { LocalsStatus } from "../../directv-design-system/organisms/channel-lineup/channel-lineup.types";
import type { LineupChannel as ModelChannel, LineupLocalsByDma } from "./lineup-model";
import type { LineupMarket } from "./resolve-lineup-for";

/**
 * Nielsen DMA codes are exactly three digits, and `LocalChannels.byDma` is keyed by them
 * (`lib/types/channels-service.ts:227`). Nielsen, not the `dma` field beside it.
 */
const NIELSEN_DMA_PATTERN = /^\d{3}$/;

/** Same guard `pages/api/local-channels.ts` applies before it touches the
 *  locals map, so the two entry points cannot disagree on what a market is. */
export function isNielsenDma(dma: unknown): dma is string {
  return typeof dma === "string" && NIELSEN_DMA_PATTERN.test(dma);
}

/** One in-market RSN row, exactly as beam sends it. `Rsntier` is all-lowercase. */
export interface BeamRsnChannel {
  channelNumber?: number | string;
  callSign?: string;
  network?: string;
  logo?: string;
  rsntier?: number | string;
  /** Team flags — GR4's badge text. Each carries team name(s), not a boolean. */
  mlb?: string;
  nhl?: string;
  nba?: string;
  wnba?: string;
  /** Plan display names this RSN is included in, e.g. "CHOICE", "ULTIMATE". Checked per entry. */
  packageNames?: unknown;
}

/**
 * Where beam's RSN artwork lives once resolved — the same directory
 * `LocalRsnChnlService.buildChannelObject` prefixes, written out here because calling that builder
 * would drag the analytics service and a dozen legacy field names (`TYPE`, `chnlNum`, `categoryId`)
 * onto this path.
 */
const RSN_LOGO_DIR = "/dtvassets/global/channel-icons/rsn-channels";

/** The segment every caller in this repo splits a beam RSN logo on before
 *  handing it to the builder (`zipcode-utils.ts:57`). */
const RSN_LOGO_SEGMENT = "rsn-channels/";

/**
 * Beam's logo field is a bare filename on some rows and a path containing `rsn-channels/` on
 * others; both appear live, which is why every existing caller splits before it prefixes.
 */
function rsnLogoUrl(logo: unknown): string | undefined {
  const raw = (textOf(logo) ?? "").trim();
  if (raw === "") {
    return undefined;
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  // The last segment: the ZIP Code's builder prefixes the directory again on a row that had it.
  const at = raw.lastIndexOf(RSN_LOGO_SEGMENT);
  const file = at >= 0 ? raw.slice(at + RSN_LOGO_SEGMENT.length) : raw;
  const bare = file.replace(/^\/+/, "");
  return bare === "" ? undefined : `${RSN_LOGO_DIR}/${bare}`;
}

/** Beam rows are untyped JSON: a field read as text is text only if it is one. */
function textOf(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function numberTextOf(value: unknown): string | undefined {
  return typeof value === "string" || typeof value === "number" ? String(value) : undefined;
}

/** GR4's team list. Each flag carries team name(s) for that league; a row with
 *  none is a regional sports network with no team attribution, not an error. */
function teamsOf(row: BeamRsnChannel): string[] {
  const teams: string[] = [];
  for (const flag of [row?.mlb, row?.nhl, row?.nba, row?.wnba]) {
    if (typeof flag !== "string") {
      continue;
    }
    for (const team of flag.split(",")) {
      const name = team.trim();
      if (name !== "" && !teams.includes(name)) {
        teams.push(name);
      }
    }
  }
  return teams;
}

function tierOf(row: BeamRsnChannel): number | undefined {
  const tier = Number(row?.rsntier);
  return Number.isFinite(tier) ? tier : undefined;
}

/** A beam RSN has no ccid, so it cannot carry one. */
function rsnId(row: BeamRsnChannel): string {
  const label = (textOf(row?.callSign) ?? textOf(row?.network) ?? "").trim();
  const number = (numberTextOf(row?.channelNumber) ?? "").trim();
  return `rsn:${label}:${number}`;
}

/**
 * One beam RSN row -> the model's own channel shape. `Packages` is deliberately empty. Beam sends
 * `packageNames` — display names — and §0's one-line rule is that membership is
 * `billingProductCode`.
 */
export function toLineupRsn(row: BeamRsnChannel | undefined): ModelChannel | null {
  if (!row || typeof row !== "object") {
    return null;
  }
  const name = (textOf(row.network) ?? textOf(row.callSign) ?? "").trim();
  if (name === "") {
    return null;
  }
  const number = numberTextOf(row.channelNumber);
  const teams = teamsOf(row);
  const packageNames = Array.isArray(row.packageNames) ?
    row.packageNames.filter((value): value is string => typeof value === "string" && value.trim() !== "") :
    [];
  return {
    id: rsnId(row),
    name,
    number,
    callSign: textOf(row.callSign),
    logoUrl: rsnLogoUrl(row.logo),
    categories: [],
    kind: "rsn",
    tier: tierOf(row),
    teams: teams.length > 0 ? teams : undefined,
    beamPackageNames: packageNames.length > 0 ? packageNames : undefined,
    myFree: false,
    fastChannel: false,
    packages: {}
  };
}

/** The beam `rsnChannels` half, mapped once. Deduped on the namespaced id so a
 *  row beam sent twice (its own `mergeChannels` key is looser) cannot render
 *  two identical RSN rows. */
export function toLineupRsns(rows: readonly BeamRsnChannel[] | undefined): ModelChannel[] {
  const seen = new Set<string>();
  const rsns: ModelChannel[] = [];
  for (const row of rows ?? []) {
    const mapped = toLineupRsn(row);
    if (!mapped || seen.has(mapped.id)) {
      continue;
    }
    seen.add(mapped.id);
    rsns.push(mapped);
  }
  return rsns;
}

/** What `sliceLocalsForDma` answers: this market's stations, and whether the
 *  lookup that produced them succeeded. */
export interface LocalsSlice {
  locals: ModelChannel[];
  status: LocalsStatus;
}

/**
 * This market's stations out of the one-key-per-platform locals catalog. `"Idle"` for "no DMA yet",
 * `"error"` for "asked and could not answer".
 */
export function sliceLocalsForDma(
  localsByDma: LineupLocalsByDma | undefined,
  dma: unknown
): LocalsSlice {
  if (dma === undefined || dma === null || dma === "") {
    return {
      locals: [],
      status: "idle"
    };
  }
  if (!isNielsenDma(dma)) {
    return {
      locals: [],
      status: "error"
    };
  }
  if (!localsByDma) {
    // No catalog at all, not a market the feed lacks.
    return {
      locals: [],
      status: "error"
    };
  }
  // Own keys only, so `constructor` / `__proto__` cannot pass as a market.
  const stations = Object.hasOwn(localsByDma, dma) ? localsByDma[dma] : undefined;
  if (!Array.isArray(stations)) {
    return {
      locals: [],
      status: "error"
    };
  }
  return {
    locals: [...stations],
    status: "idle"
  };
}

/** Everything the two lookups need, as the caller knows it. */
export interface LineupMarketInput {
  /** Locals per Nielsen DMA (`buildLineupLocals`). `undefined` is ZP11's error state for the
   *  locals rows only. */
  localsByDma?: LineupLocalsByDma;
  /** The visitor's Nielsen DMA. Absent when none is known. */
  dma?: string;
  /** The beam `rsnChannels` half, raw. `undefined` means no beam call was made
   *  this render, which is `"idle"` — not a failure. */
  rsnChannels?: readonly BeamRsnChannel[];
  /** The beam call was made and failed. ZP11's error state for the RSN rows
   *  ONLY; locals keep whatever status their own lookup gave them. */
  rsnChannelsFailed?: boolean;
}

/**
 * Both ZIP-scoped sources, resolved into the one object `resolveLineupFor` takes. One object, not
 * two arguments, and that is the point — see `LineupMarket`.
 */
export function resolveLineupMarket(input: LineupMarketInput = {}): LineupMarket {
  // Each source in its own block. Neither reads the other's outcome, so a
  // failure on one cannot empty or re-status the other (§5, G1/G3).
  const { locals, status: localChannelsStatus } = sliceLocalsForDma(
    input?.localsByDma,
    input?.dma
  );

  let rsns: ModelChannel[] = [];
  let rsnsStatus: LocalsStatus = "idle";
  if (input?.rsnChannelsFailed === true) {
    rsnsStatus = "error";
  } else if (input?.rsnChannels !== undefined) {
    rsns = toLineupRsns(input.rsnChannels);
  }

  return {
    locals,
    rsns,
    dma: input?.dma,
    localChannelsStatus,
    rsnsStatus
  };
}
